import ChatFailureType from "@mapmycustomers/shared/enum/chat/ChatFailureType";
import ChatMessageRole from "@mapmycustomers/shared/enum/chat/ChatMessageRole";
import ChatUserMessageStatus from "@mapmycustomers/shared/enum/chat/ChatUserMessageStatus";
import ChatMessage from "@mapmycustomers/shared/types/chat/ChatMessage";
import User from "@mapmycustomers/shared/types/User";

import { ChatProposalUiState } from "@app/util/chat/proposalRowStatus";
import loggingService from "@app/util/logging";

// sessionStorage rather than localStorage on purpose: the thread belongs to the tab, so a
// new tab is a fresh conversation and a stale thread cannot outlive the tab that held it.
export const CHAT_CONVERSATION_KEY = "__MMC_CHAT_CONVERSATION_V1__";

export const MAX_STORED_MESSAGES = 50;

export interface StoredChatConversation {
  conversationId?: number;
  messages: ChatMessage[];
  proposalUi?: Record<number, ChatProposalUiState>;
  userId: User["id"];
}

const ROLES: string[] = [ChatMessageRole.ASSISTANT, ChatMessageRole.LOCAL, ChatMessageRole.USER];

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const isMessageShaped = (value: unknown): value is ChatMessage => {
  if (!isRecord(value)) {
    return false;
  }
  const { id, createdAt, role } = value;
  return typeof id === "string" && typeof createdAt === "string" && ROLES.includes(role as string);
};

// A message still marked sending was interrupted by the reload. The turn may well have
// completed server-side — BE commits the reply before writing the response, and there is no
// history endpoint to fetch it — so it is offered as retryable with its own failure type,
// which is what earns it a different tooltip from an outright failure.
const settleRestored = (message: ChatMessage): ChatMessage =>
  message.role === ChatMessageRole.USER && message.status === ChatUserMessageStatus.SENDING
    ? {
        ...message,
        failureType: ChatFailureType.RESTORED,
        status: ChatUserMessageStatus.FAILED,
      }
    : message;

class ChatConversationStorage {
  clear = () => {
    try {
      sessionStorage.removeItem(CHAT_CONVERSATION_KEY);
    } catch (error) {
      loggingService.error("Failed to clear the stored chat conversation", error);
    }
  };

  getState = (userId: User["id"]): Omit<StoredChatConversation, "userId"> | undefined => {
    try {
      const raw = sessionStorage.getItem(CHAT_CONVERSATION_KEY);
      if (typeof raw !== "string") {
        return undefined;
      }
      const parsed: unknown = JSON.parse(raw);
      if (typeof parsed !== "object" || parsed === null) {
        return undefined;
      }
      const stored = parsed as Record<string, unknown>;
      // The second of the two user-change guards: this one holds even when the first (the
      // prevUserId check in store/app/sagas.ts) is not reached, and onInitialize has
      // several early returns.
      if (stored.userId !== userId) {
        return undefined;
      }
      const messages = Array.isArray(stored.messages)
        ? stored.messages.filter(isMessageShaped).map(settleRestored)
        : [];
      return {
        conversationId:
          typeof stored.conversationId === "number" ? stored.conversationId : undefined,
        messages,
        proposalUi: isRecord(stored.proposalUi)
          ? (stored.proposalUi as Record<number, ChatProposalUiState>)
          : {},
      };
    } catch (error) {
      // Logged like setState and clear are: corrupt stored JSON means the rep silently loses a
      // thread, and swallowing that leaves nothing to explain why.
      loggingService.error("Failed to read the stored chat conversation", error);
      return undefined;
    }
  };

  setState = ({ conversationId, messages, proposalUi, userId }: StoredChatConversation) => {
    try {
      sessionStorage.setItem(
        CHAT_CONVERSATION_KEY,
        JSON.stringify({
          conversationId,
          // Counting messages rather than turns keeps the trim from bisecting a turn. A trim
          // can strand proposalUi entries for messages that fall off the front — harmless dead
          // keys, not worth reconciling against the trimmed list.
          messages: messages.slice(-MAX_STORED_MESSAGES),
          proposalUi,
          userId,
        })
      );
    } catch (error) {
      // Quota exceeded, or Safari private browsing. Degrade to "not persisted this session"
      // rather than letting the throw escape into the saga that called us.
      loggingService.error("Failed to persist the chat conversation", error);
    }
  };
}

const chatConversationStorage = new ChatConversationStorage();
export default chatConversationStorage;
